import cn from 'classnames';

import { STANDARD_AS_PACKAGED_ACCESSIBILITY_LABEL, HEAT_AND_EAT_SLUG } from 'lib/constants';

const SingleColumnNutritionTableHeadingRows = ({
  isTempo,
  menuCategory,
  dailyValueAsPackaged,
  dailyValueAsPrepared,
}) => {
  const isReadyToHeat = isTempo || menuCategory === HEAT_AND_EAT_SLUG;
  const abbr = isReadyToHeat
    ? 'Nutritional values for a prepared serving'
    : STANDARD_AS_PACKAGED_ACCESSIBILITY_LABEL;
  const calories = isTempo ? dailyValueAsPrepared.calories : dailyValueAsPackaged.calories;
  const caloriesAbbr = isTempo
    ? `${dailyValueAsPrepared.calories} as prepared calories`
    : `${dailyValueAsPackaged.calories} as packaged calories`;

  const thtdBaseStyle = 'px-[3px] pb-[2px] pt-[3px] align-baseline leading-none';
  const caloriesStyle = 'helveticaBlack px-[3px] pb-[2px] pt-0 align-baseline leading-none';

  return (
    <tbody>
      <tr className="border-0">
        <th className={thtdBaseStyle} scope="col" colSpan="3">
          <span className="sr-only">Nutrient type</span>
        </th>
      </tr>
      <tr className="border-0">
        <th className={thtdBaseStyle} scope="col" abbr={abbr} colSpan="3">
          <span className="helveticaBlack">Amount per serving</span>
        </th>
      </tr>
      <tr className="border-x-0 border-b-4 border-t-0 border-solid border-b-black-100">
        <th className={cn(caloriesStyle, 'text-[1.875em]')} scope="col">
          Calories
        </th>
        <td
          className={cn(caloriesStyle, 'text-right text-[2.25em]')}
          colSpan="2"
          abbr={caloriesAbbr}
        >
          {calories}
        </td>
      </tr>
    </tbody>
  );
};

SingleColumnNutritionTableHeadingRows.propTypes = {
  isTempo: PropTypes.bool.isRequired,
  menuCategory: PropTypes.string.isRequired,
  dailyValueAsPackaged: PropTypes.object.isRequired,
  dailyValueAsPrepared: PropTypes.object.isRequired,
};

const ThreeColumnNutritionTableHeadingRows = ({ dailyValueAsPackaged, dailyValueAsPrepared }) => {
  const rowStyle =
    'p-0 [&>*:first-child]:w-1/3 [&>*:first-child]:border-l-0 [&>*:last-child]:w-1/3 [&>*:last-child]:border-r-0 [&>*:last-child]:pr-0';
  const thtdBaseStyle =
    'px-[3px] pb-[2px] pt-[3px] align-baseline leading-none helveticaBlack border-black-100';
  const thickBottomBorder =
    'border border-t-0 border-solid pt-0 border-b-[6px] relative border-b-transparent after:content-[""] after:block after:absolute after:bottom-[-6px] after:left-[3px] after:right-[3px] after:bg-black-100 after:h-[6px]';
  const asTextStyle =
    'border-b-0 border-l border-r-0 border-t-0 border-solid !pb-0 text-right text-[0.875em]';
  const calorieValueStyle = 'text-right text-[2.25em]';

  return (
    <tbody>
      <tr className={rowStyle}>
        <th className={cn(thtdBaseStyle, asTextStyle)} scope="col">
          <span className="sr-only">Nutrient type</span>
        </th>
        <th
          className={cn(thtdBaseStyle, asTextStyle)}
          scope="col"
          colSpan="2"
          abbr={STANDARD_AS_PACKAGED_ACCESSIBILITY_LABEL}
        >
          <span className="whitespace-nowrap">
            As packaged<sup className="align-bottom text-[100%]">**</sup>
          </span>
        </th>
        <th
          className={cn(thtdBaseStyle, asTextStyle)}
          colSpan="2"
          abbr="Nutritional values for prepared meal (excluding salt & olive oil)"
          scope="col"
        >
          <span className="whitespace-nowrap">As Prepared</span>
        </th>
      </tr>
      <tr className={cn('border-t-0', rowStyle)}>
        <th
          className={cn(thtdBaseStyle, thickBottomBorder, 'text-left text-[1.875em]')}
          scope="col"
        >
          Calories
        </th>
        <td className={cn(thtdBaseStyle, thickBottomBorder, calorieValueStyle)} colSpan="2">
          {dailyValueAsPackaged.calories}
        </td>
        <td className={cn(thtdBaseStyle, thickBottomBorder, calorieValueStyle)} colSpan="2">
          {dailyValueAsPrepared.calories}
        </td>
      </tr>
    </tbody>
  );
};

ThreeColumnNutritionTableHeadingRows.propTypes = {
  dailyValueAsPackaged: PropTypes.object.isRequired,
  dailyValueAsPrepared: PropTypes.object.isRequired,
};

const NutritionTableHeadingRows = ({
  isTempo,
  menuCategory,
  needsAllNutritionDetails,
  dailyValueAsPackaged,
  dailyValueAsPrepared,
}) => {
  if (needsAllNutritionDetails) {
    return (
      <ThreeColumnNutritionTableHeadingRows
        dailyValueAsPackaged={dailyValueAsPackaged}
        dailyValueAsPrepared={dailyValueAsPrepared}
      />
    );
  }

  return (
    <SingleColumnNutritionTableHeadingRows
      isTempo={isTempo}
      menuCategory={menuCategory}
      dailyValueAsPackaged={dailyValueAsPackaged}
      dailyValueAsPrepared={dailyValueAsPrepared}
    />
  );
};

NutritionTableHeadingRows.propTypes = {
  isTempo: PropTypes.bool.isRequired,
  menuCategory: PropTypes.string.isRequired,
  needsAllNutritionDetails: PropTypes.bool.isRequired,
  dailyValueAsPackaged: PropTypes.object.isRequired,
  dailyValueAsPrepared: PropTypes.object.isRequired,
};

export default NutritionTableHeadingRows;
